import type { AuthRouterContext } from "@my-langguth/auth-ui";
import {
  ErrorComponent,
  Link,
  Outlet,
  createRootRouteWithContext,
  useMatches,
} from "@tanstack/react-router";
import { AppShell, ProductSwitcher } from "@my-langguth/shell";
import { useTranslation } from "react-i18next";
import { getProductSwitcherUrls } from "@/lib/product-switcher-urls";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/ui/sonner";
import { CustomerCartProvider } from "@/components/shopping-cart-provider";
import ShoppingCart from "@/components/shopping-cart";
import { UserMenu } from "@/components/user-menu";
import { CustomerIdProvider } from "@/components/customer-id-provider";
import { CustomerSelect } from "@/components/customer-select";
import { useAuth } from "@/hooks/auth";
import React from "react";
import { NotFound } from "@/components/not-found";
import {
  requireShopAuth,
  SHOP_PUBLIC_ROUTE_IDS,
} from "@/lib/shop-auth-guard";
import { LegalFooter } from "@/components/legal-footer";
import { cn } from "@/lib/utils";

const SHOP_MACHINE_ROUTE_ID = "/customers/$customerId/machines/$machineId";
const SHOP_AUTH_ROUTE_IDS = new Set([
  "/login",
  "/request-password-reset",
  "/reset-password",
  "/new-user-setup",
]);

export const Route = createRootRouteWithContext<AuthRouterContext>()({
  beforeLoad: async ({ context, location, matches }) => {
    const visitingPublicRoute = matches.some((m) =>
      SHOP_PUBLIC_ROUTE_IDS.has(String(m.routeId)),
    );
    if (visitingPublicRoute) return;

    await requireShopAuth({ context, location });
  },
  component: RootComponent,
  errorComponent: ErrorComponent,
  notFoundComponent: NotFound,
});

function RootComponent() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const matches = useMatches();
  const hideHeader = matches.some((match) =>
    SHOP_PUBLIC_ROUTE_IDS.has(String(match.routeId)),
  );
  const fillViewport = matches.some(
    (match) => String(match.routeId) === SHOP_MACHINE_ROUTE_ID,
  );
  const fillAuthColumn = matches.some((match) =>
    SHOP_AUTH_ROUTE_IDS.has(String(match.routeId)),
  );
  const fillColumn = fillViewport || fillAuthColumn;
  const showDevTools = import.meta.env.DEV;

  const { shopUrl, checklistenUrl, flowManagerUrl } = getProductSwitcherUrls();

  const header =
    !hideHeader ? (
      <header className={cn("flex shrink-0 flex-wrap items-center gap-3")}>
        <div className="order-1 flex items-center gap-2">
          <ProductSwitcher
            current="shop"
            shopUrl={shopUrl}
            checklistenUrl={checklistenUrl}
            flowManagerUrl={
              user?.role === "langguth_admin" ? flowManagerUrl : undefined
            }
            labels={{
              menuTitle: t("appSwitcher.menuTitle"),
              shop: t("appSwitcher.shop"),
              checklists: t("appSwitcher.checklists"),
              flowManager: t("appSwitcher.flowManager"),
              triggerAria: t("appSwitcher.triggerAria"),
              currentBadge: t("appSwitcher.currentBadge"),
            }}
          />
          <h1 className="text-2xl font-semibold">
            <Link to="/">
              <span className="text-green-600">myLangguth</span> Shop
            </Link>
          </h1>
        </div>

        <div className="order-2 ml-auto flex items-center md:order-3 md:ml-0">
          <ShoppingCart></ShoppingCart>
          {isAuthenticated ? <UserMenu /> : null}
        </div>

        <div className="order-3 basis-full md:order-2 md:ml-auto md:basis-auto">
          <CustomerSelect />
        </div>
      </header>
    ) : null;

  return (
    <CustomerIdProvider>
      <CustomerCartProvider>
        <Toaster theme="light" richColors />
        <AppShell
          header={header}
          footer={<LegalFooter />}
          className={cn(
            fillColumn && "h-dvh",
            fillViewport && "overflow-hidden",
          )}
        >
          <Outlet />
          {showDevTools && (
            <>
              <TanStackRouterDevtools position="bottom-left" />
              <ReactQueryDevtools></ReactQueryDevtools>
            </>
          )}
        </AppShell>
      </CustomerCartProvider>
    </CustomerIdProvider>
  );
}

// Simple error boundary component
class ErrorBoundary extends React.Component<
  { onError: (error: unknown) => void; children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      return null; // ErrorComponent will be rendered by RootComponent
    }
    return this.props.children;
  }
}
